import { setPageDescription } from "../seo";
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import QuoteRequestModal from "./QuoteRequestModal";

// Sub-service detail page — a single content block (title, rich content, CTA, sticky image +
// related sub-services), fed by SubServiceCard data. Rendered as the one block below the parent
// service's banner, so it deliberately has no separate hero of its own (no duplicate image/title).
export default function SubServiceDetail({ subService, backSlug, relatedItems }) {
  const [showQuote, setShowQuote] = useState(false);
  const [typedTitle, setTypedTitle] = useState("");

  // Typewriter effect: reveal the title one character at a time, matching the site's other heroes.
  useEffect(() => {
    const text = subService.subtitle || "";
    setTypedTitle("");
    let i = 0;
    const typeInterval = setInterval(() => {
      i += 1;
      setTypedTitle(text.slice(0, i));
      if (i >= text.length) clearInterval(typeInterval);
    }, 70);
    return () => clearInterval(typeInterval);
  }, [subService.subtitle]);

  useEffect(() => {
    document.title = `${subService.subtitle} - Mr. Aali Online Technologies`;
    setPageDescription(`${subService.subtitle}: ${(subService.how_to_develop || "").replace(/<[^>]*>/g, " ").replace(/&nbsp;/g, " ")}`);
  }, [subService.subtitle]);

  return (
    <section className="relative bg-navy text-white py-12 md:py-16">
      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <Link to={`/${backSlug}`} className="inline-block text-accent-light text-sm mb-4 hover:underline">
          &larr; Back to {subService.title}
        </Link>

        <div className="grid md:grid-cols-[60%_40%] gap-12 items-start">
          {/* Left column: content */}
          <div>
            <h1 className="text-3xl md:text-5xl font-extrabold tracking-tight">
              {typedTitle}
              <span className="typing-cursor inline-block w-[3px] ml-1 bg-accent-light align-middle" style={{ height: "0.85em" }} />
            </h1>

            {subService.how_to_develop && (
              <div
                className="mt-8 text-gray-300 leading-relaxed [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:text-white [&_h1]:mt-8 [&_h1]:mb-3 [&_h2]:text-xl [&_h2]:font-bold [&_h2]:text-white [&_h2]:mt-8 [&_h2]:mb-3 [&_p]:mb-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-3 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-3 [&_li]:mb-1 [&_a]:text-accent-light [&_strong]:text-white"
                dangerouslySetInnerHTML={{ __html: subService.how_to_develop }}
              />
            )}

            <button
              onClick={() => setShowQuote(true)}
              className="mt-8 px-6 py-3 rounded-full bg-brand-gradient text-white font-semibold shadow-md shadow-accent/20"
            >
              Request Quote &amp; Information
            </button>
          </div>

          {/* Right column: image + related sub-services */}
          <div className="md:sticky md:top-24">
            <div className="relative rounded-xl overflow-hidden bg-navy-dark border border-accent-light/25 shadow-[0_0_25px_rgba(34,211,238,0.2)]">
              {subService.image ? (
                <img src={subService.image} alt={subService.subtitle} className="w-full h-auto block bg-navy" />
              ) : (
                <div className="w-full h-72 md:h-80 bg-brand-gradient" />
              )}
            </div>

            {relatedItems.length > 0 && (
              <div className="mt-6">
                <p className="text-xs uppercase tracking-widest text-white/50 font-semibold mb-4">
                  Sub Services
                </p>
                <div className="flex flex-col gap-3">
                  {relatedItems.map((item, i) => (
                    <motion.div
                      key={item.id}
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: i * 0.08 }}
                      whileHover={{ scale: 1.02 }}
                    >
                      <Link
                        to={`/${item.slug}`}
                        className="block w-full px-4 py-3 rounded-md bg-white/5 border border-navy-border text-gray-200 text-sm hover:border-accent-light hover:text-accent-light transition-colors"
                      >
                        {item.title}
                      </Link>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {showQuote && (
        <QuoteRequestModal serviceTitle={subService.subtitle} onClose={() => setShowQuote(false)} />
      )}
    </section>
  );
}
